"use client";

import { Calendar, HeartPulse, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Process() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Calendar,
    title: "Schedule your visit",
    description: "Select a convenient time for our technicians to arrive. No delays, no complex scheduling.",
    number: "01"
  }, {
    icon: MessageCircle,
    title: "Expert consultation",
    description: "Discuss your equipment needs with our certified specialists who prioritize your uptime.",
    number: "02"
  }, {
    icon: HeartPulse,
    title: "Optimized solution",
    description: "Receive a comprehensive maintenance report, genuine parts, and clear operational guidance.",
    number: "03"
  }];
  return <section data-section-id="9812" id="how-it-works" className="relative bg-background ![padding:50px_0px_144px_0px]">
      <div className="mx-auto max-w-7xl px-6 md:px-16">
        {}
        <div ref={header.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-[900ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Our workflow
          </p>
          <h2 className="font-default mt-4 text-[clamp(2rem,3.8vw,3.25rem)] font-bold leading-[1.1] tracking-[-0.018em] text-foreground">
            Your path to{" "}
            <span className={`font-default italic text-primary transition-opacity duration-500 ${header.isVisible ? "opacity-100" : "opacity-0"}`} style={{
            transitionDelay: header.isVisible ? "200ms" : "0ms"
          }}>
              efficiency
            </span>
          </h2>
          <p className="font-default mt-6 text-[clamp(1.125rem,1.3vw,1.25rem)] font-normal leading-[1.65] text-muted-foreground">
            Three streamlined steps from your initial request to a fully optimized industrial solution.
          </p>
        </div>

        {}
        <div className="relative mt-24 md:mt-[96px]">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 hidden h-px bg-border md:block" style={{
          top: "calc(28px + 24px)"
        }} />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <StepCard key={i} data-index={i} index={i} Icon={Icon} title={step.title} description={step.description} number={step.number} />;
          })}
          </div>
        </div>
      </div>
    </section>;
}
type StepCardProps = {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  number: string;
  "data-index"?: number;
};
function StepCard({
  index,
  Icon,
  title,
  description,
  number,
  ...rest
}: StepCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={rest["data-index"]} className={`relative transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="group h-full bg-card border border-border rounded-lg shadow-md p-7 hover:shadow-lg transition-shadow duration-[400ms] ease-out">
        <CardContent className="px-0 flex h-full flex-col">
          <div className="relative inline-flex h-12 w-12 items-center justify-center">
            <Icon className="h-12 w-12 text-primary transition-colors duration-300 group-hover:text-primary/80" strokeWidth={1.5} />
          </div>

          <h3 className="font-default mt-8 text-[clamp(1.5rem,2.2vw,2rem)] font-semibold leading-[1.2] tracking-[-0.012em] text-foreground">
            {title}
          </h3>

          <p className="font-default mt-3 text-base font-normal leading-[1.7] text-muted-foreground">
            {description}
          </p>

          <div className="mt-auto pt-6">
            <span className="font-default text-sm font-bold tracking-[0.01em] text-primary">
              {number}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>;
}